import { FormSkeleton } from '@globaleris/ui.library'
import { List, ListItem, Typography } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import moment from 'moment'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { EnumCommodity, EnumDivisa, EnumEstadoCliente, EnumTipoContrato } from '../../../../api'
import { enumTranslateOption } from '../../../../helpers'
import { ContractFormRaw, NewContractProduct } from '../../../contracts/contracts.model'

const useStyles = makeStyles(() => ({
  list: {
    '& > *': {
      display: 'table'
    }
  }
}))

interface ProductsQuotesLeftPanelDetailsProps {
  isLoading?: boolean
  productData: NewContractProduct
  contractData?: Partial<ContractFormRaw> | null
  commodity: EnumCommodity
}

export const QuotesProductsLeftPanelDetails: React.FC<ProductsQuotesLeftPanelDetailsProps> = ({
  isLoading = false,
  productData,
  contractData,
  commodity
}) => {
  const classes = useStyles()
  const [t] = useTranslation(['globalerisLibrary', 'enums', 'quotes'])

  const { productCode, productDescription, currency = 1 } = productData
  const {
    type: contractType,
    contractCode,
    startDate,
    endDate,
    signatureDate,
    state
  } = contractData || {}

  return (
    <>
      {isLoading ? (
        <FormSkeleton withTitle={true} elevation={0} columns={1} inputs={10} />
      ) : (
        <>
          <Typography variant="h1" color="primary" noWrap>
            {t('quotes:components.quotesProducts.leftColumn.title')}
          </Typography>

          {/* Product Section */}
          <List className={classes.list} key={'productList'}>
            <ListItem disableGutters key={'productList-1'}>
              <Typography variant="h2">
                {t('quotes:components.quotesProducts.leftColumn.productDetails')}
              </Typography>
            </ListItem>

            <ListItem divider disableGutters key={'productList-2'}>
              <Typography variant="caption">
                {t('quotes:components.quotesProducts.leftColumn.captions.code')}
              </Typography>
              <Typography variant="body2">{productCode}</Typography>
            </ListItem>

            <ListItem divider disableGutters key={'productList-3'}>
              <Typography variant="caption">
                {t('quotes:components.quotesProducts.leftColumn.captions.description')}
              </Typography>
              <Typography variant="body2">{productDescription}</Typography>
            </ListItem>

            <ListItem divider disableGutters key={'productList-4'}>
              <Typography variant="caption">
                {t('quotes:components.quotesProducts.leftColumn.captions.currency')}
              </Typography>
              <Typography variant="body2">
                {enumTranslateOption(currency, EnumDivisa, 'enums:EnumDivisa')}
              </Typography>
            </ListItem>

            {commodity && (
              <ListItem divider disableGutters key={'productList-5'}>
                <Typography variant="caption">
                  {t('quotes:components.quotesProducts.leftColumn.captions.commodity')}
                </Typography>
                <Typography variant="body2">
                  {enumTranslateOption(commodity, EnumCommodity, 'enums:EnumCommodity')}
                </Typography>
              </ListItem>
            )}
          </List>

          {/* Contract Section */}
          <List className={classes.list} key={'contractList'}>
            <ListItem disableGutters key={'contractList-1'}>
              <Typography variant="h2">
                {t('quotes:components.quotesProducts.leftColumn.contractDetails')}
              </Typography>
            </ListItem>

            <ListItem divider disableGutters key={'contractList-2'}>
              <Typography variant="caption">
                {t('quotes:components.quotesProducts.leftColumn.captions.contractType')}
              </Typography>
              <Typography variant="body2">
                {enumTranslateOption(contractType, EnumTipoContrato, 'enums:EnumTipoContrato')}
              </Typography>
            </ListItem>

            <ListItem divider disableGutters key={'contractList-3'}>
              <Typography variant="caption">
                {t('quotes:components.quotesProducts.leftColumn.captions.contractCode')}
              </Typography>
              <Typography variant="body2">{contractCode}</Typography>
            </ListItem>

            <ListItem divider disableGutters key={'contractList-4'}>
              <Typography variant="caption">
                {t('quotes:components.quotesProducts.leftColumn.captions.startDate')}
              </Typography>
              <Typography variant="body2">
                {moment(startDate).format('YYYY-MM-DD') || ''}
              </Typography>
            </ListItem>

            {endDate && (
              <ListItem divider disableGutters key={'contractList-5'}>
                <Typography variant="caption">
                  {t('quotes:components.quotesProducts.leftColumn.captions.endDate')}
                </Typography>
                <Typography variant="body2">
                  {moment(endDate).format('YYYY-MM-DD') || ''}
                </Typography>
              </ListItem>
            )}

            <ListItem divider disableGutters key={'contractList-6'}>
              <Typography variant="caption">
                {t('quotes:components.quotesProducts.leftColumn.captions.signatureDate')}
              </Typography>
              <Typography variant="body2">
                {moment(signatureDate).format('YYYY-MM-DD') || ''}
              </Typography>
            </ListItem>

            <ListItem divider disableGutters key={'contractList-7'}>
              <Typography variant="caption">
                {t('quotes:components.quotesProducts.leftColumn.captions.state')}
              </Typography>
              <Typography variant="body2">
                {enumTranslateOption(state, EnumEstadoCliente, 'enums:EnumEstadoCliente')}
              </Typography>
            </ListItem>
          </List>
        </>
      )}
    </>
  )
}
